import { httpsCallable } from 'firebase/functions';
import { create } from 'zustand';
import { functions } from '../../../shared/config/firebase';
import { useOnboardingOcdThemesStore } from './useOnboardingOcdThemesStore';

interface GenerateOnboardingQuestionRequest {
  step: number;
  previousQuestionAnswers: { question: string; answer: string }[];
  mostDistressingTheme: string;
}

interface GenerateOnboardingQuestionResponse {
  question: OcdThemeDetailsQuestion;
  hasMoreQuestions: boolean;
}

export interface OcdThemeDetailsQuestion {
  ocdProfileKey: string;
  question: string;
  subtitle: string;
  options: {
    value: string;
    label: string;
  }[];
}

export interface OcdThemeDetailsQuestionTemplate {
  questionTemplate: string;
  questionContext: string;
  subtitle: string;
  ocdProfileKey: string;
}

export interface OnboardingOcdDetailsState {
  currentStep: number;
  hasMoreQuestions: boolean;
  currentQuestion: OcdThemeDetailsQuestion;
  previousQuestionAnswers: { question: string; answer: string }[];
  mostDistressingTheme: string;
  init: (userOcdThemes: string[]) => void;
  generateNextOnboardingQuestion: (previousAnswer: string) => Promise<void>;
}

const initialState = {
  currentStep: -1,
  hasMoreQuestions: true,
  currentQuestion: {
    ocdProfileKey: '',
    question: '',
    subtitle: '',
    options: [],
  },
  previousQuestionAnswers: [],
  mostDistressingTheme: '',
};

export const useOnboardingOcdDetailsStore = create<OnboardingOcdDetailsState>()((set, get) => ({
  ...initialState,
  init: (userOcdThemes: string[]) => {
    // Get the themes from the OCD themes store
    const getOcdThemes = useOnboardingOcdThemesStore.getState().getOcdThemes;
    const userThemes = getOcdThemes(userOcdThemes);

    // Create the first question - asking user to select their most distressing theme
    const firstQuestion: OcdThemeDetailsQuestion = {
      ocdProfileKey: 'onboarding_most_distressing_theme',
      question: 'What is the theme that is bothering you the most right now?',
      subtitle: "Don't worry, if you don't know for sure, just choose which one is closest now.",
      options: userThemes.map((theme) => ({
        value: theme.key,
        label: theme.friendlyName,
      })),
    };

    set({
      currentStep: 0,
      currentQuestion: firstQuestion,
      hasMoreQuestions: true,
    });
  },
  generateNextOnboardingQuestion: async (answer: string) => {
    const currentState = get();
    const currentStep = currentState.currentStep;
    const nextStep = currentStep + 1;
    const currentQuestion = currentState.currentQuestion;
    const previousQuestionAnswers = currentState.previousQuestionAnswers;
    let mostDistressingTheme = currentState.mostDistressingTheme;

    // If on step 0, the answer is the selected OCD theme
    if (currentStep === 0) {
      set({ mostDistressingTheme: answer });
      mostDistressingTheme = answer;
    }

    // Only add to previous answers if we have a valid current question (not the initial state)
    const newPreviousQuestionAnswers =
      currentStep >= 0 && currentQuestion.question
        ? [...previousQuestionAnswers, { question: currentQuestion.question, answer: answer }]
        : previousQuestionAnswers;

    const request: GenerateOnboardingQuestionRequest = {
      step: nextStep - 1, // Adjust: step 1 should request template at index 0
      previousQuestionAnswers: newPreviousQuestionAnswers,
      mostDistressingTheme: mostDistressingTheme,
    };
    const generateOnboardingQuestionCallable = httpsCallable<GenerateOnboardingQuestionRequest, GenerateOnboardingQuestionResponse>(
      functions,
      'generateOnboardingQuestion'
    );
    const result = await generateOnboardingQuestionCallable(request);
    const response = result.data;
    set({
      currentQuestion: response.question,
      currentStep: nextStep,
      previousQuestionAnswers: newPreviousQuestionAnswers,
      hasMoreQuestions: response.hasMoreQuestions,
    });
  },
}));
